import React, { useState, CSSProperties, memo, useCallback } from "react";
import BasicDataTable from "../../../Component/BasicComponents/BasicDataTable";
import RouteModal from "../../../Component/BasicComponents/Modal/RouteModal";
import { FaEdit, FaEye, FaPlus } from "react-icons/fa";
import ExpenseHeadModal from "./Modals/ExpenseHeadModal";
import { useALLExpese } from "./hooks/useExpense";

type Props = {};

interface TableButtonProps {
  onClick: () => void;
  title?: string;
  style?: CSSProperties;
}

type ExpenseApiResponse = {
  ExpenseId: number;
  ExpenseName: string;
  Policykeyc?: string;
  Trips?: number | null;
  IsMandatory?: number | null;
  DefaultPolicykey?: string;
};

const TableButton: React.FC<TableButtonProps> = memo(
  ({ onClick, title = "Add Heads", style }) => {
    const defaultStyle: CSSProperties = {
      display: "flex",
      borderRadius: "20px",
      padding: "2px",
      width: "110px",
      height: "40px",
      justifyContent: "center",
      textAlign: "center",
      alignItems: "center",
    };

    return (
      <div>
        <button
          onClick={onClick}
          className="btn btn-info"
          style={{ ...defaultStyle, ...style }} // External styles override default
        >
          <FaPlus /> <span style={{ marginLeft: "5px" }}>{title}</span>
        </button>
      </div>
    );
  }
);

const ExpenseHeadTable = (props: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState<
    number | undefined
  >(undefined);
  const [mode, setMode] = useState<"view" | "edit" | "add">("add");

  const openModal = (newMode: "view" | "edit" | "add", expenseId?: number) => {
    setMode(newMode);
    setSelectedExpenseId(expenseId);
    setIsModalOpen(true);
  };

  const { data, isLoading, isSuccess } = useALLExpese({ enable: true });

  const ExpenseRequestData =
    isSuccess && Array.isArray(data?.data?.expenseData)
      ? data?.data.expenseData.map(
          (item: ExpenseApiResponse, index: number) => ({
            ExpenseId: item.ExpenseId,
            Sr: index + 1,
            Title: item.ExpenseName,
            Policy: item.DefaultPolicykey,
            Trips: item.Trips,
            Mandatory: item.IsMandatory,
          })
        )
      : [];

  const ExpeseRequestColumns = (
    handleExpanseRequestView: (row: any) => void,
    handleExpanseRequestEdit: (row: any) => void
  ) => [
    {
      name: "Sr.",
      selector: (row: { Sr: number | string }) => row.Sr,
      maxWidth:'50px'
    },
    {
      name: "Title",
      selector: (row: { Title: string }) => row.Title,
    },
    {
      name: "Policy",
      selector: (row: { Policy: string }) => row.Policy,
    },
    {
      name: "Trips",
      selector: (row: { Trips: string }) => row.Trips,
      maxWidth:'300px',
      Wrap:true
      
    },
    {
      name: "Mandatory",
      selector: (row: { Mandatory: string }) => row.Mandatory,
    },
    {
      name: "Actions",
      width:'200px',
      cell: (row: any) => (
        <div
          className="d-flex align-items-center gap-2" /* style={{ display: "flex", gap: "4px" }} */
        >
          <button
            onClick={() => handleExpanseRequestEdit(row.ExpenseId)}
            className="btn btn-primary"
            style={{
              borderRadius: "20px",
              padding: "5px",
              width: "80px",
            }}
          >
            <FaEdit style={{ fill: "#fff" }} size={16} /> Edit
          </button>
          <button
            onClick={() => handleExpanseRequestView(row.ExpenseId)}
            className="btn btn-info"
            style={{ borderRadius: "20px", padding: "5px", width: "80px" }}
          >
            <FaEye size={18} style={{ fill: "#fff" }} /> View
          </button>
        </div>
      ),
    },
  ];

  const handleExpanseRequestView = (ExpenseId: number) => {
    setMode("view");
    setSelectedExpenseId(ExpenseId);
    setIsModalOpen(true);
  };

  const handleExpanseRequestEdit = (ExpenseId: number) => {
    setIsModalOpen(true);
    setSelectedExpenseId(ExpenseId);
    setMode("edit");
  };

  const handleAddHeads = useCallback(() => {
    setIsModalOpen(true);
    setMode("add");
    setSelectedExpenseId(undefined);
  }, []);

  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const handleClose = () => setIsModalOpen(false);

  return (
    <div className="row mb-0">
      <div className="col-sm-12">
        <BasicDataTable
          showSpinner={isLoading}
          Title="Expense Head"
          data={!isLoading ? ExpenseRequestData : isLoading}
          columns={ExpeseRequestColumns(
            handleExpanseRequestView,
            handleExpanseRequestEdit
          )}
          headerContent={
            <TableButton
              onClick={() => handleAddHeads()}
              title="Add Head"
              style={{ marginRight: "8px" }}
            />
          }
        />
      </div>

      {/* modal 1 Head*/}
      <RouteModal
        size="xl"
        isOpen={isModalOpen}
        toggle={toggleModal}
        title={
          mode === "view"
            ? "View Expense Head"
            : mode === "edit"
            ? "Edit Expense Head"
            : "Add Expense Head"
        }
        content={
          <ExpenseHeadModal
            expenseId={selectedExpenseId}
            handleClose={handleClose}
            mode={mode}
            trips={data?.data?.trips}
          />
        }
      />
    </div>
  );
};

export default ExpenseHeadTable;
